import React, { useState, useEffect } from 'react';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardDateTimePicker } from '@material-ui/pickers';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import TextField from '@material-ui/core/TextField';
import DateFnsUtils from '@date-io/date-fns';
import * as styles from './styles/styles.module.scss';
import { localeMap, localizedFormat } from '../../../utils/time_helpers';

const TextFieldComponent = props => {
  return <TextField {...props} disabled />;
};

const greenTint = '#69A041';
const green = '#428A13';
const greenShade = '#32690F';
const secondaryDarkGray = '#414141';

const fontFamily = 'Proxima Nova, Arial, sans-serif';

const palette = {
  primary: {
    light: greenTint,
    main: green,
    dark: greenShade
  }
};

const typography = {
  body1: {
    fontFamily,
    color: secondaryDarkGray
  },
  body2: {
    fontFamily,
    color: secondaryDarkGray
  },
  caption: {
    fontFamily
  }
};



// if we want the picker to be full width, dynamically
// create the dimmensions for the calendar
const getFullWidthSizing = (fullWidth, pickerWidth) => {
  let paperProps = {};
  let overrides = {};

  if (!fullWidth || !pickerWidth) {
    return { paperProps, overrides };
  }

  paperProps = {
    style: { width: pickerWidth, height: pickerWidth * 0.84 }
  };

  const dayWidth = pickerWidth * 0.12;

  overrides = {
    MuiPickersBasePicker: {
      pickerView: {
        minWidth: '100%',
        'overflow-x': 'visible'
      }
    },
    MuiPickersCalendarHeader: {
      dayLabel: {
        width: dayWidth
      },
      iconButton: {
        height: dayWidth
      }
    },
    MuiPickersDay: {
      day: {
        width: dayWidth,
        height: dayWidth
      },
      current: {
        background: '#f2f2f2',
        color: '#414141'
      }
    }
  };

  return { paperProps, overrides };
};

const DatePicker = props => {
  const [isOpen, toggleOpen] = useState(false);
  const [pickerWidth, updatePickerWidth] = useState(null);
  const KeyboardIcon = isOpen ? ExpandLess : ExpandMore;
  const iconStyles = isOpen ? styles.activeIcon : styles.icon;
  const rootStyles = isOpen ? styles.expandedRoot : styles.root;

  useEffect(() => {
    if (props.fullWidth) {
      updatePickerWidth(document.getElementById('keyboardDatePicker').clientWidth);
    }
  }, [isOpen]);

  const { paperProps, overrides } = getFullWidthSizing(props.fullWidth, pickerWidth);

  const theme = createMuiTheme({
    palette,
    typography,
    overrides
  });

  const PickerComponent = props.showTime ? KeyboardDateTimePicker : KeyboardDatePicker;

  return (
    <div id="keyboardDatePicker">
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[props.locale]}>
          <PickerComponent
            disableToolbar
            variant="inline"
            onClick={() => toggleOpen(true)}
            onClose={() => toggleOpen(false)}
            open={isOpen}
            classes={{ root: `${rootStyles} ${props.slim ? styles.slim : ''} ${props.materialRootClass}` }}
            disableFuture={props.disableFuture}
            disablePast={props.disablePast}
            format={localizedFormat(props.abbreviateMonth, props.locale, props.showTime)}
            margin="normal"
            id="date-picker-inline"
            value={props.date}
            onChange={props.onChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
              classes: { root: styles.icon },
              disableRipple: true
            }}
            InputProps={{
              disableUnderline: true,
              classes: { root: styles.textInput }
            }}
            PopoverProps={{
              classes: { root: styles.popoverRoot },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: -16,
                horizontal: 14
              },
              getContentAnchorEl: null,
              PaperProps: paperProps
            }}
            keyboardIcon={<KeyboardIcon className={iconStyles} />}
            TextFieldComponent={TextFieldComponent}
          />
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </div>
  );
};

DatePicker.defaultProps = {
  onChange: () => {},
  maxDate: new Date(),
  locale: 'en-US',
  disableFuture: true,
  disablePast: false,
  fullWidth: false,
  abbreviateMonth: false,
  showTime: false,
};

export default DatePicker;
